<template>
  <div class="flex rounded-md shadow-sm">
    <div class="relative flex flex-grow items-stretch focus-within:z-10">
      <input type="text" :class="inputClass" :value="fileName" :placeholder="t('placeholder')" readonly />
      <input ref="inputFileRef" class="hidden" :accept="fileTypes" type="file" @change="onChange" />
    </div>

    <button
      v-if="fileName"
      class="text-neutral-40 border-neutral-90 hover:bg-neutral-99 focus:border-primary-40 focus:ring-primary-40 relative -ml-px inline-flex items-center space-x-2 whitespace-nowrap rounded-r-md border bg-neutral-100 px-4 py-2 text-sm font-medium focus:outline-none focus:ring-1"
      @click.prevent="clearFile"
    >
      <TrashIcon class="text-neutral-80 mr-2 h-5 w-5" aria-hidden="true" />
      {{ t("delete") }}
    </button>

    <button
      v-else
      class="border-neutral-90 hover:bg-neutral-99 focus:border-primary-40 focus:ring-primary-40 relative -ml-px inline-flex items-center space-x-2 whitespace-nowrap rounded-r-md border bg-neutral-100 px-4 py-2 text-sm font-medium focus:outline-none focus:ring-1"
      :disabled="loading"
      :loading="loading"
      @click.prevent="uploadFile"
    >
      <UploadIcon v-if="!loading" class="text-neutral-80 mr-2 h-5 w-5" aria-hidden="true" />
      <span v-if="!loading" class="text-neutral-40">{{ t("upload") }}</span>
      <LoadingSpinner v-if="loading" class="text-primary-40 mr-2 h-5 w-5" aria-hidden="true" />
      <span v-if="loading" class="text-neutral-80">{{ t("uploading") }}</span>
    </button>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { UploadIcon, TrashIcon } from "@heroicons/vue/solid";
import LoadingSpinner from "@atoms/LoadingSpinner.vue";

// Props
const props = withDefaults(
  defineProps<{
    modelValue: File;
    fileName: string;
    fileTypes: string;
    loading: boolean;
    error: string;
  }>(),
  {
    modelValue: () => ({}) as File,
    fileName: "",
    fileTypes: "",
    loading: false,
    error: "",
  },
);

// Language
const { t } = useI18n();

// Emits
const emit = defineEmits<{
  (e: "update:modelValue", value: File): void;
  (e: "clear"): void;
}>();

// Events
const inputFileRef = ref<HTMLInputElement | null>(null);

// Methods
const onChange = (e: Event) => {
  const eventFiles = (e.target as HTMLInputElement).files;
  const file = eventFiles?.item(0);
  if (file) {
    emit("update:modelValue", file);
  }
};

const uploadFile = () => {
  if (inputFileRef.value) {
    inputFileRef.value.click();
  }
};

const clearFile = () => {
  emit("clear");
};

const inputClass = computed(() => {
  const classes: string[] = [];

  classes.push("block w-full rounded-none rounded-l-md pl-4 sm:text-sm");

  if (props.error) {
    classes.push("border-danger-80 text-danger-5 placeholder-danger-80 focus:ring-danger-50 focus:border-danger-50");
  } else {
    classes.push("border-neutral-90 focus:ring-primary-40 focus:border-primary-40");
  }

  return classes.join(" ");
});
</script>

<i18n lang="json">
{
  "es": {
    "upload": "Subir",
    "uploading": "Subiendo...",
    "delete": "Eliminar",
    "placeholder": ".png, .gif, .jpeg"
  },
  "pt": {
    "upload": "Enviar",
    "uploading": "Enviando...",
    "delete": "Excluir",
    "placeholder": ".png, .gif, .jpeg"
  }
}
</i18n>
